import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { db, supabase } from '../supabase'
import { Edit3, SkipForward, Sparkles, Loader2 } from 'lucide-react'
import Confetti from 'react-confetti'

const RewritePage = () => {
  const { user } = useAuth()
  const [currentReminder, setCurrentReminder] = useState(null)
  const [rewriteText, setRewriteText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showConfetti, setShowConfetti] = useState(false)
  const [rewritesToday, setRewritesToday] = useState(0)
  const [noMoreReminders, setNoMoreReminders] = useState(false)
  const [typedText, setTypedText] = useState('')

  const moods = {
    random: { emoji: '🎲', label: 'Random' },
    poetic: { emoji: '📝', label: 'Poetic' },
    funny: { emoji: '😂', label: 'Funny' },
    serious: { emoji: '🎯', label: 'Serious' },
    weird: { emoji: '🤪', label: 'Weird' }
  }

  const loadRandomReminder = useCallback(async () => {
    setIsLoading(true)
    try {
      const reminder = await db.getRandomUnrewrittenReminder(user.id)
      setCurrentReminder(reminder)
      setNoMoreReminders(false)
    } catch (error) {
      console.error('Error loading random reminder:', error)
      setNoMoreReminders(true)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const loadRewritesToday = useCallback(async () => {
    try {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const { data, error } = await supabase
        .from('rewrites')
        .select('created_at')
        .eq('rewriter_id', user.id)
        .gte('created_at', today.toISOString())
      if (error) throw error
      setRewritesToday(data.length)
    } catch (error) {
      console.error('Error loading rewrites today:', error)
    }
  }, [user])

  useEffect(() => {
    if (user) {
      loadRandomReminder()
      loadRewritesToday()
    }
  }, [user, loadRandomReminder, loadRewritesToday])

  useEffect(() => {
    if (!currentReminder?.text) return;
    setTypedText('');
    let i = 0;
    const interval = setInterval(() => {
      setTypedText(currentReminder.text.slice(0, i + 1));
      i++;
      if (i >= currentReminder.text.length) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [currentReminder?.text]);

  // AI Assist (mocked)
  useEffect(() => {
    if (!currentReminder?.id) return;
    const timer = setTimeout(() => {
      if (!currentReminder.rewritten && !currentReminder.aiRewrite) {
        setCurrentReminder(prev => ({
          ...prev,
          aiRewrite: {
            text: 'The apples await, crisp and green, a memory of home unseen. (AI Rewrite)',
            created_at: new Date().toISOString(),
          },
        }));
      }
    }, 30000); // 30 seconds for demo
    return () => clearTimeout(timer);
  }, [currentReminder?.id, currentReminder?.rewritten, currentReminder?.aiRewrite]);

  const handleSubmitRewrite = async (e) => {
    e.preventDefault()
    if (!rewriteText.trim() || rewriteText.length > 150) {
      alert('Please enter a rewrite with 150 characters or less.')
      return
    }
    setIsSubmitting(true)
    try {
      await db.createRewrite(currentReminder.id, user.id, rewriteText.trim())
      setRewriteText('')
      setShowConfetti(true)
      // Mark reminder as rewritten (mocked for now)
      // In real implementation, update the reminder in the DB and remove from pool
      setCurrentReminder(null)
      setTimeout(() => setShowConfetti(false), 3000)
      await loadRandomReminder()
      await loadRewritesToday()
    } catch (error) {
      console.error('Error creating rewrite:', error)
      alert('Failed to submit rewrite. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkip = async () => {
    await loadRandomReminder()
  }

  const handleShare = async () => {
    try {
      // Create shareable text
      const shareText = `Check out this creative rewrite on Strangeminder! 🧠\n\nOriginal: "${currentReminder?.text}"\nRewrite: "${rewriteText}"\n\nJoin the community at strangeminder.com`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'Strangeminder Rewrite',
          text: shareText,
          url: window.location.origin
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText);
        alert('Rewrite copied to clipboard! Share it with your friends! 📋');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Share feature coming soon!');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4" size={32} />
          <p className="text-gray-600">Finding a reminder for you...</p>
        </div>
      </div>
    )
  }

  if (noMoreReminders) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            No more reminders to rewrite!
          </h1>
          <p className="text-gray-600 mb-6">
            You've helped rewrite all available reminders. Check back later for new ones!
          </p>
          <button
            onClick={loadRandomReminder}
            className="btn-primary"
          >
            Check Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 md:py-8">
      {showConfetti && <Confetti />}
      
      {/* Stats */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full">
          <Sparkles size={16} />
          <span className="text-sm font-medium">
            {rewritesToday} rewrites today
          </span>
        </div>
      </div>

      {/* Current Reminder Card */}
      <div className="card mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {currentReminder?.user?.avatar_url ? (
              <img
                src={currentReminder.user.avatar_url}
                alt={currentReminder.user.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-medium">
                  {currentReminder?.user?.name?.charAt(0) || '?'}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-gray-800">
                {currentReminder?.user?.name || 'Anonymous'}
              </p>
              <p className="text-sm text-gray-500">needs your help</p>
            </div>
          </div>
          
          {currentReminder?.mood && currentReminder.mood !== 'random' && (
            <div className="text-right">
              <div className="text-2xl">{moods[currentReminder.mood].emoji}</div>
              <p className="text-xs text-gray-500">{moods[currentReminder.mood].label}</p>
            </div>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-6 mb-4">
          <p className="text-2xl font-medium text-gray-800 text-center">
            "{typedText}"
          </p>
        </div>

        <p className="text-sm text-gray-600 text-center">
          Add emotional or funny context to this reminder
        </p>

        {currentReminder?.aiRewrite && (
          <div className="mt-4 p-3 bg-primary-50 rounded-lg border border-primary-200 animate-pulse">
            <span className="block text-xs text-primary-600 font-bold mb-1">AI Rewrite</span>
            <span className="block text-gray-700">{currentReminder.aiRewrite.text}</span>
          </div>
        )}
      </div>

      {/* Rewrite Form */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <Edit3 className="mr-2" size={20} />
          Your creative rewrite
        </h2>

        <form onSubmit={handleSubmitRewrite} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your rewrite (max 150 characters)
            </label>
            <textarea
              value={rewriteText}
              onChange={(e) => setRewriteText(e.target.value)}
              placeholder="e.g., Granny Smiths, like grandpa loved. Make sure they squeak when you bite."
              className="input-field resize-none h-24"
              maxLength={150}
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-sm text-gray-500">
                {rewriteText.length}/150 characters
              </p>
              <p className="text-xs text-gray-400">
                Be creative, emotional, or funny!
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={isSubmitting || !rewriteText.trim()}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Give it meaning'}
            </button>
            
            <button
              type="button"
              onClick={handleSkip}
              disabled={isSubmitting}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SkipForward size={16} />
            </button>
          </div>
        </form>
        <button
          type="button"
          className="btn-primary mt-4 w-full"
          onClick={handleShare}
        >
          Share
        </button>
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-blue-50 rounded-xl">
        <h3 className="font-medium text-blue-800 mb-2">💡 Rewrite Tips</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Add emotional context or personal meaning</li>
          <li>• Make it funny or unexpected</li>
          <li>• Connect it to memories or feelings</li>
          <li>• Be creative and original!</li>
        </ul>
      </div>
    </div>
  )
}

export default RewritePage 